import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import BotanicalArt from "@/components/BotanicalArt";

const ARTICLES = [
  {
    tag: "Starting out",
    title: "Do I need therapy?",
    body: "There's no threshold you have to cross. If something is taking up space in your head — a loop you can't stop, a heaviness you can't name, a relationship that keeps hurting — that's reason enough. Therapy isn't only for crises; it's for anyone tired of carrying things alone. A useful question isn't \"is it bad enough?\" but \"would talking help?\" If you're even asking, one conversation is worth trying.",
  },
  {
    tag: "Starting out",
    title: "What happens in the first therapy session?",
    body: "Mostly, your therapist listens. They'll ask what brought you in and what you'd like to be different, at whatever pace you're comfortable with. You don't need to prepare, perform, or tell your whole story in one sitting. Many first-timers worry they'll cry or freeze — both are fine, and both happen often. You set the pace; they hold the space.",
  },
  {
    tag: "Practical",
    title: "How much does therapy cost in India?",
    body: "Private therapy in India typically ranges from ₹500 to ₹3,000+ per session, depending on the therapist's experience and city. Eunoia keeps it simple: your first session is ₹99 — the same honest price for every therapist. Affordable shouldn't mean low quality; it should mean the price never becomes the reason you stop.",
  },
  {
    tag: "Practical",
    title: "Is online therapy confidential?",
    body: "At Eunoia, yes. Sessions run over secure video or audio, and we don't store your chats or keep session histories. Nothing appears on shared family accounts or insurance records. Confidentiality is also a professional obligation for your psychologist — what you say stays in the room, with narrow legal exceptions only where someone is at risk of serious harm.",
  },
  {
    tag: "Understanding yourself",
    title: "Therapy for overthinking",
    body: "Replaying conversations, catastrophising tomorrow, lying awake litigating the past — overthinking feels productive but solves nothing; it's anxiety wearing a disguise. Therapy helps you notice the loops as they start, understand what they're protecting you from, and build ways to step out of them. Approaches like CBT and ACT are particularly effective here.",
  },
  {
    tag: "Understanding yourself",
    title: "How to choose a therapist",
    body: "Start with practical fit: language, availability, and whether they've worked with what you're carrying. Then read how they describe their work — do they sound like someone you could talk to? The single biggest predictor of therapy working is the relationship itself, so give yourself permission to switch if the first fit isn't right. It's not failure; it's the process working.",
  },
];

export default function Resources() {
  return (
    <div className="mx-auto max-w-screen-2xl px-6 py-16 md:px-12 md:py-24 lg:px-24" data-testid="resources-page">
      <div className="grid items-center gap-10 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-accent">
            <span className="h-px w-10 bg-accent" /> Resources
          </p>
          <h1 className="mt-6 max-w-3xl font-serif text-4xl font-bold leading-tight tracking-tight md:text-6xl">
            Honest answers, before you commit to anything.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            No jargon, no lecture. Just the things people quietly Google at 1 AM — answered plainly.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="hidden lg:col-span-5 lg:block">
          <BotanicalArt variant="tangle" caption="Untangle it, one thought at a time." />
        </Reveal>
      </div>

      <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {ARTICLES.map((a, i) => (
          <Reveal key={a.title} delay={(i % 3) * 0.08}>
            <article
              className="group flex h-full flex-col rounded-2xl border border-primary/10 bg-card p-8 transition-all duration-500 hover:-translate-y-1 hover:border-accent/50"
              data-testid={`resource-${a.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-accent">{a.tag}</p>
              <h2 className="mt-4 font-serif text-2xl font-semibold leading-snug tracking-tight">{a.title}</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{a.body}</p>
              <div className="mt-auto pt-6">
                <ArrowUpRight className="h-5 w-5 text-accent transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-20 flex flex-wrap items-center justify-between gap-6 rounded-3xl bg-primary p-10 text-primary-foreground md:p-14" data-testid="resources-cta-band">
          <p className="max-w-lg font-serif text-2xl font-bold leading-tight tracking-tight md:text-4xl">
            Reading helps. Talking helps more.
          </p>
          <Link
            to="/find"
            data-testid="resources-cta-find"
            className="group flex items-center gap-2 rounded-full bg-primary-foreground px-8 py-4 text-sm font-bold text-primary transition-transform duration-300 hover:scale-[1.04] active:scale-95"
          >
            Find someone to talk to
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </Reveal>
    </div>
  );
}
